
import * as React from "react";
import { cn } from "@/lib/utils";
import { ChartSkeleton, ChartTooltip, HatchDefs, TooltipRow, monotonePath, series, useChartWidth, type Pt, type SeriesSlot } from "./core";

/* ── Flow stream: mirrored layered bands, value pills above/below ───────── */

export interface FlowPoint {
  label: string;
  top: number;
  bottom: number;
}

export function FlowStreamChart({
  data,
  topLabel,
  bottomLabel,
  slot = 2,
  height = 300,
  format,
  compactFormat = format,
  checkpoints,
  ariaLabel,
}: {
  data: FlowPoint[];
  topLabel: string;
  bottomLabel: string;
  slot?: SeriesSlot;
  height?: number;
  format: (v: number) => string;
  /** Short format for the pills (fits narrow widths). */
  compactFormat?: (v: number) => string;
  /** Indices that get a permanent marker with value pills. Defaults to 3 evenly spaced. */
  checkpoints?: number[];
  ariaLabel: string;
}) {
  const { ref, width, ready } = useChartWidth();
  const gid = React.useId().replace(/:/g, "");
  const [hover, setHover] = React.useState<number | null>(null);
  if (!ready) return <ChartSkeleton height={height} innerRef={ref} />;
  const n = data.length;
  const color = series(slot);

  const pillH = 28;
  const pad = { l: 0, r: 0, t: pillH + 10, b: pillH + 30 };
  const iw = width, ih = height - pad.t - pad.b;
  const cy = pad.t + ih / 2;
  const peak = Math.max(...data.map((d) => Math.max(d.top, d.bottom)));
  const s = (ih / 2 - 2) / (peak || 1);
  const x = (i: number) => (i / (n - 1)) * iw;

  const layers = [1, 0.7, 0.4];
  const layerOpacity = [0.32, 0.55, 0.95];
  const band = (f: number) => {
    const top: Pt[] = data.map((d, i) => [x(i), cy - Math.max(3, d.top * s * f)]);
    const bot: Pt[] = data.map((d, i): Pt => [x(i), cy + Math.max(3, d.bottom * s * f)]).reverse();
    return `${monotonePath(top)}${monotonePath(bot, false)}Z`;
  };

  const marks = checkpoints ?? (n >= 6 ? [Math.round(n * 0.2), Math.round(n * 0.52), n - 2] : [0, Math.floor(n / 2), n - 1]);
  const pillW = Math.min(96, Math.max(64, iw / 7));
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 70))));

  function Pill({ cx, y, text }: { cx: number; y: number; text: string }) {
    const px = Math.min(Math.max(cx - pillW / 2, 2), iw - pillW - 2);
    return (
      <g>
        <rect x={px} y={y} width={pillW} height={pillH} rx={pillH / 2} filter={`url(#${gid}-shadow)`} style={{ fill: "var(--ads-surface)" }} />
        <text x={px + pillW / 2} y={y + pillH / 2} dy="0.35em" textAnchor="middle" className="fill-ink font-mono text-[12px] font-medium tabular-nums">
          {text}
        </text>
      </g>
    );
  }

  function onMove(e: React.PointerEvent<SVGRectElement>) {
    const box = e.currentTarget.getBoundingClientRect();
    setHover(Math.round(((e.clientX - box.left) / box.width) * (n - 1)));
  }

  return (
    <div ref={ref} className="relative w-full select-none">
      <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block overflow-visible">
        <defs>
          <linearGradient id={`${gid}-h`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.75 }} />
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 1 }} />
          </linearGradient>
          <radialGradient id={`${gid}-glow`} cx="18%" cy="50%" r="55%">
            <stop offset="0%" style={{ stopColor: series(4), stopOpacity: 0.22 }} />
            <stop offset="100%" style={{ stopColor: series(4), stopOpacity: 0 }} />
          </radialGradient>
          <filter id={`${gid}-shadow`} x="-20%" y="-40%" width="140%" height="180%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" style={{ floodColor: "var(--ads-ink)" }} />
          </filter>
          <filter id={`${gid}-blur`} x="-10%" y="-30%" width="120%" height="160%">
            <feGaussianBlur stdDeviation="14" />
          </filter>
        </defs>

        <rect x={0} y={pad.t - 20} width={iw} height={ih + 40} fill={`url(#${gid}-glow)`} />
        <path d={band(1.08)} filter={`url(#${gid}-blur)`} style={{ fill: color, fillOpacity: 0.18 }} />
        {layers.map((f, k) => (
          <path key={f} d={band(f)} fill={`url(#${gid}-h)`} style={{ fillOpacity: layerOpacity[k] }} />
        ))}

        {/* direct labels (identity is never colour-only) */}
        <text x={4} y={pad.t + 2} className="fill-muted text-[11px] font-semibold uppercase tracking-[0.06em]">↑ {topLabel}</text>
        <text x={4} y={pad.t + ih - 2} className="fill-muted text-[11px] font-semibold uppercase tracking-[0.06em]">↓ {bottomLabel}</text>

        {(hover === null ? marks : [hover]).map((i) => (
          <g key={i}>
            <line x1={x(i)} x2={x(i)} y1={pad.t - 6} y2={pad.t + ih + 6} strokeWidth={2.5} style={{ stroke: "var(--ads-surface)" }} />
            <Pill cx={x(i)} y={2} text={compactFormat(data[i].top)} />
            <Pill cx={x(i)} y={pad.t + ih + 8} text={compactFormat(data[i].bottom)} />
          </g>
        ))}

        {data.map((d, i) =>
          (i === n - 1 || (i % every === 0 && n - 1 - i >= every)) ? (
            <text key={d.label} x={Math.min(Math.max(x(i), 14), iw - 14)} y={height - 4} textAnchor="middle" className="fill-muted font-mono text-[11px]">
              {d.label}
            </text>
          ) : null
        )}
        <rect x={0} y={pad.t} width={iw} height={ih} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} style={{ cursor: "crosshair" }} />
      </svg>
      {hover !== null && (
        <ChartTooltip x={x(hover)} y={cy} containerWidth={width}>
          <div className="mb-1 text-white/60">{data[hover].label}</div>
          <TooltipRow label={topLabel} value={format(data[hover].top)} />
          <TooltipRow label={bottomLabel} value={format(data[hover].bottom)} />
          <TooltipRow label="Net" value={format(data[hover].top - data[hover].bottom)} />
        </ChartTooltip>
      )}
      <div className="sr-only"><table>
        <caption>{ariaLabel}</caption>
        <thead><tr><th>Period</th><th>{topLabel}</th><th>{bottomLabel}</th></tr></thead>
        <tbody>{data.map((d) => <tr key={d.label}><th scope="row">{d.label}</th><td>{format(d.top)}</td><td>{format(d.bottom)}</td></tr>)}</tbody>
      </table></div>
    </div>
  );
}

/* ── Highlight bars with average flag (Total Balance / Balance reference) ─ */

export interface BarDatum {
  label: string;
  value: number;
  detail?: string;
}

export function HighlightBarChart({
  data,
  height: minHeight = 250,
  slot = 1,
  format,
  avgLabel,
  defaultActive,
  fill = false,
  ariaLabel,
}: {
  data: BarDatum[];
  height?: number;
  slot?: SeriesSlot;
  format: (v: number) => string;
  /** Text for the average flag, e.g. (avg) => `Avg ${fmt(avg)}`. */
  avgLabel: (avg: number) => string;
  defaultActive?: number;
  /** Grow to the parent's height (the parent must give it room, e.g. flex-1). `height` becomes the minimum. */
  fill?: boolean;
  ariaLabel: string;
}) {
  const { ref, width, height: measured, ready } = useChartWidth();
  const gid = React.useId().replace(/:/g, "");
  const n = data.length;
  const [active, setActive] = React.useState(defaultActive ?? n - 1);
  // Accessible table view — rendered in the skeleton phase too, so the values are never chart-only.
  const table = (
    <div className="sr-only"><table>
      <caption>{ariaLabel}</caption>
      <tbody>{data.map((d, i) => <tr key={i}><th scope="row">{d.detail ? `${d.label} · ${d.detail}` : d.label}</th><td>{format(d.value)}</td></tr>)}</tbody>
    </table></div>
  );
  if (!ready) return <><ChartSkeleton height={minHeight} innerRef={ref} />{table}</>;
  const height = fill ? Math.max(minHeight, measured) : minHeight;
  const color = series(slot);
  const max = Math.max(...data.map((d) => d.value));
  const avg = data.reduce((a, d) => a + d.value, 0) / n;

  const avgText = avgLabel(avg);
  const flagW = avgText.length * 6.6 + 22;
  // Reserve the flag's width so it never sits on top of a bar.
  const pad = { l: flagW + 6, r: 0, t: 34, b: 38 };
  const iw = width - pad.l, ih = height - pad.t - pad.b;
  const slotW = iw / n;
  const barW = Math.min(52, slotW * 0.72);
  const y = (v: number) => pad.t + ih - (v / (max * 1.05)) * ih;
  const cx = (i: number) => pad.l + slotW * i + slotW / 2;
  // Thin x labels by the longest label's estimated width; the active one always shows.
  const labelW = Math.max(...data.map((d) => d.label.length)) * 6.6 + 16;
  const labelEvery = Math.max(1, Math.ceil(labelW / slotW));
  const showLabel = (i: number) => i === active || ((n - 1 - i) % labelEvery === 0 && Math.abs(i - active) >= labelEvery);

  return (
    <div ref={ref} className="relative w-full select-none" style={fill ? { height: "100%", minHeight } : undefined}>
      <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block overflow-visible">
        <defs>
          <HatchDefs id={`${gid}-hatch`} />
          <linearGradient id={`${gid}-active`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.55 }} />
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 1 }} />
          </linearGradient>
        </defs>

        {data.map((d, i) => {
          const top = y(d.value);
          const h = pad.t + ih - top;
          const isActive = i === active;
          return (
            <g key={d.label}>
              <rect
                x={cx(i) - barW / 2}
                y={top}
                width={barW}
                height={Math.max(h, 8)}
                rx={Math.min(12, barW / 2)}
                fill={isActive ? `url(#${gid}-active)` : `url(#${gid}-hatch)`}
                strokeWidth={isActive ? 0 : 1}
                style={{ stroke: "var(--ads-line)", transition: "y 200ms, height 200ms" }}
              />
              {isActive && (
                <text x={cx(i)} y={top - 10} textAnchor="middle" className="fill-ink text-[13px] font-semibold tabular-nums">
                  {format(d.value)}
                </text>
              )}
              <g>
                {isActive && (() => {
                  const pw = d.label.length * 6.8 + 18;
                  return <rect x={cx(i) - pw / 2} y={height - 28} width={pw} height={24} rx={12} style={{ fill: "var(--ads-primary-soft)" }} />;
                })()}
                {showLabel(i) && (
                  <text x={cx(i)} y={height - 12} textAnchor="middle" className={cn("text-[12px]", isActive ? "fill-primary font-semibold" : "fill-muted")}>
                    {d.label}
                  </text>
                )}
              </g>
              <rect
                x={pad.l + slotW * i}
                y={pad.t - 30}
                width={slotW}
                height={ih + 30}
                fill="transparent"
                tabIndex={0}
                role="button"
                aria-label={`${d.label}${d.detail ? ` · ${d.detail}` : ""}: ${format(d.value)}`}
                aria-pressed={isActive}
                onPointerEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => setActive(i)}
                className="cursor-pointer outline-none"
              />
            </g>
          );
        })}

        {/* average rule + black flag */}
        <line x1={flagW + 4} x2={width} y1={y(avg)} y2={y(avg)} strokeDasharray="1.5 4" strokeLinecap="round" strokeWidth={1.5} style={{ stroke: "var(--ads-ink)" }} />
        <path
          d={`M0,${y(avg) - 11}h${flagW - 8}l8,11l-8,11h-${flagW - 8}z`}
          style={{ fill: "var(--ads-ink)" }}
          className="pointer-events-none"
        />
        <text x={8} y={y(avg)} dy="0.35em" className="pointer-events-none fill-white text-[11px] font-medium tabular-nums">{avgText}</text>
      </svg>
      {table}
    </div>
  );
}
